import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { isStaleChunk, reloadOnceForStaleChunk } from '@/lib/staleChunk';
import { Button } from '@/ui/Button';

/**
 * What the router shows when a page throws.
 *
 * React Router catches render and loader errors itself and shows its own
 * developer screen, so the app-level ErrorBoundary never sees them. This is
 * the same recovery screen, wired in as the router's `errorElement`.
 *
 * Deliberately depends on nothing but the router: it renders outside the
 * AuthProvider, and an error page that can itself fail is no error page.
 *
 * It never shows the raw error - file paths and stack text mean nothing to the
 * person using the app and leak how it is built. Details go to the console.
 */

function describe(error) {
  if (isRouteErrorResponse(error) && error.status === 404) {
    return {
      title: 'Page not found',
      message: "The page you're looking for doesn't exist or has moved.",
      canReload: false,
    };
  }
  if (isStaleChunk(error)) {
    return {
      title: 'A new version is available',
      message: 'This page was updated while you had it open. Reload to get the latest version.',
      canReload: true,
    };
  }
  return {
    title: 'Something went wrong',
    message: 'This page ran into a problem. Reloading usually fixes it.',
    canReload: true,
  };
}

export function RouteError() {
  const error = useRouteError();
  const { title, message, canReload } = describe(error);
  // An out-of-date page file fixes itself with one reload; only if that was
  // already tried does the recovery screen appear.
  const [reloading, setReloading] = useState(() => isStaleChunk(error));

  useEffect(() => {
    if (import.meta.env.DEV) {
      // The router swallows the error; keep it visible in the console.
      console.error('Route error', error);
    }
    if (isStaleChunk(error) && !reloadOnceForStaleChunk()) setReloading(false);
  }, [error]);

  if (reloading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface-muted p-6">
        <p className="flex items-center gap-2 text-sm text-content-muted" role="status">
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the latest version…
        </p>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-muted p-6">
      <div className="w-full max-w-md rounded-card bg-surface p-8 text-center shadow-card">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-danger/10">
          <AlertTriangle className="h-6 w-6 text-danger" aria-hidden="true" />
        </span>
        <h1 className="mt-4 text-lg font-semibold text-content">{title}</h1>
        <p className="mt-2 text-sm text-content-muted">{message}</p>

        <div className="mt-6 flex flex-col gap-2 sm:flex-row">
          {canReload && (
            <Button
              fullWidth
              leftIcon={<RefreshCw className="h-4 w-4" aria-hidden="true" />}
              onClick={() => window.location.reload()}
            >
              Reload page
            </Button>
          )}
          {/* A plain link, not <Link>: a full load starts the app clean,
              which is the point after an error. */}
          <Button
            as="a"
            href="/"
            variant="secondary"
            fullWidth
            leftIcon={<Home className="h-4 w-4" aria-hidden="true" />}
          >
            Go to home page
          </Button>
        </div>
      </div>
    </main>
  );
}
